import { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { persistor, RootState } from '../../../redux/store';
import { useFormik } from 'formik';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { authActions } from '../../../redux/slices/auth.slice';
import { ROUTES } from '../../../helpers/constans';
import { loginSchema } from '../../../helpers/formik-shemas/auth.schema';
import AuthLayout from '../components/auth-layout';
import AuthTextField from '../components/auth-text-field';
import { styles } from '../components/auth-layout.styles';

interface IFormValues {
  email: string;
  password: string;
  isPersist: boolean;
}

const Login = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authToken: string | undefined = useSelector((state: RootState) => state.auth.token);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const errorMessage = useSelector((state: RootState) => state.auth.errorMessage);

  const formik = useFormik<IFormValues>({
    initialValues: { email: '', password: '', isPersist: true },
    validationSchema: loginSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: ({ email, password, isPersist }) => {
      if (!isPersist) {
        persistor.pause();
      }
      dispatch(authActions.login({ email, password }));
    },
  });

  useEffect((): void => {
    if (authToken) {
      navigate(ROUTES.home);
    }
  }, [authToken, navigate]);

  useEffect(() => {
    return () => {
      dispatch(authActions.clearAuthFeedback());
    };
  }, [dispatch]);

  return (
    <AuthLayout title="Sign in" subtitle="Enter your email and password to access your XDOS workspace.">
      <Box
        component="form"
        sx={styles.form}
        onSubmit={formik.handleSubmit}
        noValidate
        aria-busy={isLoading}
      >
        {errorMessage ? (
          <Alert severity="error" sx={styles.alert} role="alert">
            {errorMessage}
          </Alert>
        ) : null}

        <AuthTextField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          formik={formik}
          disabled={isLoading}
          icon={<MailOutlineIcon fontSize="small" sx={styles.fieldIcon} />}
        />
        <AuthTextField
          name="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          formik={formik}
          disabled={isLoading}
          icon={<LockOutlinedIcon fontSize="small" sx={styles.fieldIcon} />}
        />

        <Box sx={styles.optionsRow}>
          <FormControlLabel
            sx={styles.remember}
            control={
              <Switch
                name="isPersist"
                checked={formik.values.isPersist}
                onChange={formik.handleChange}
                disabled={isLoading}
                inputProps={{ 'aria-label': 'Remember me' }}
              />
            }
            label="Remember me"
          />
          <Typography
            component="button"
            type="button"
            sx={{ ...styles.textLink, border: 0, background: 'none', p: 0 }}
            onClick={() => navigate(ROUTES.resetPassword)}
          >
            Forgot password?
          </Typography>
        </Box>

        <Button type="submit" sx={styles.submit} disabled={isLoading} fullWidth>
          {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
        </Button>
      </Box>

      <Typography sx={styles.footer}>
        Don&apos;t have an account?{' '}
        <Typography component={Link} to={ROUTES.registration} sx={styles.textLink}>
          Sign up
        </Typography>
      </Typography>
    </AuthLayout>
  );
};

export default Login;
